import React, { useState, useEffect, useCallback } from "react";
import {
  Calendar,
  Video,
  Users,
  Clock,
  Link2,
  RefreshCw,
  X,
  CheckCircle,
  AlertCircle,
  Plus,
  Trash2,
  Search,
  Building2,
  Power,
  PowerOff,
} from "lucide-react";
import api from "../../services/api";

const TeamsIntegration = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionDetails, setConnectionDetails] = useState(null);
  const [managedUsers, setManagedUsers] = useState([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [callbackStatus, setCallbackStatus] = useState(null);

  // Add-user modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedToAdd, setSelectedToAdd] = useState({});
  const [addingUsers, setAddingUsers] = useState(false);

  useEffect(() => {
    const run = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const teamsStatus = urlParams.get("teams");
      const error = urlParams.get("error");

      if (error) {
        setCallbackStatus({
          type: "error",
          message: `Connection failed: ${error}`,
        });
        setTimeout(() => {
          window.history.replaceState({}, document.title, "/teams");
          setCallbackStatus(null);
        }, 5000);
      } else if (teamsStatus === "connected") {
        setCallbackStatus({
          type: "success",
          message: "Teams tenant connected successfully!",
        });
        setTimeout(() => {
          window.history.replaceState({}, document.title, "/teams");
          setCallbackStatus(null);
        }, 3000);
      }

      await checkConnectionStatus();
    };
    run();
  }, []);

  const checkConnectionStatus = async () => {
    try {
      setLoading(true);
      const resp = await api.getTeamsStatus();
      setIsConnected(!!resp.connected);
      setConnectionDetails(resp.details || null);

      if (resp.connected) {
        await Promise.all([fetchManagedUsers(), fetchUpcomingMeetings()]);
      } else {
        setManagedUsers([]);
        setUpcomingMeetings([]);
      }
    } catch (e) {
      setCallbackStatus({ type: "error", message: "Failed to check status" });
    } finally {
      setLoading(false);
    }
  };

  const connectTeams = async () => {
    try {
      setLoading(true);
      const resp = await api.connectTeams();
      if (resp.authUrl) window.location.href = resp.authUrl;
    } catch (e) {
      setCallbackStatus({
        type: "error",
        message: e.response?.data?.message || e.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const disconnectTeams = async () => {
    if (
      !window.confirm(
        "Disconnect Teams tenant? All managed users will stop syncing.",
      )
    )
      return;

    try {
      setLoading(true);
      await api.disconnectTeams();
      setIsConnected(false);
      setConnectionDetails(null);
      setManagedUsers([]);
      setUpcomingMeetings([]);
      setCallbackStatus({ type: "success", message: "Disconnected" });
      setTimeout(() => setCallbackStatus(null), 3000);
    } catch (e) {
      setCallbackStatus({
        type: "error",
        message: e.response?.data?.message || e.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const syncAll = async () => {
    try {
      setSyncing(true);
      const resp = await api.syncTeamsAll();
      await fetchManagedUsers();
      await fetchUpcomingMeetings();
      setCallbackStatus({
        type: "success",
        message: `Synced ${resp.success}/${resp.total} users · ${resp.meetings} meetings`,
      });
      setTimeout(() => setCallbackStatus(null), 3500);
    } catch (e) {
      setCallbackStatus({
        type: "error",
        message: e.response?.data?.message || e.message,
      });
    } finally {
      setSyncing(false);
    }
  };

  const syncOne = async (managedUserId) => {
    try {
      await api.syncManagedTeamsUser(managedUserId);
      await fetchManagedUsers();
      setCallbackStatus({ type: "success", message: "User synced" });
      setTimeout(() => setCallbackStatus(null), 2500);
    } catch (e) {
      setCallbackStatus({
        type: "error",
        message: e.response?.data?.message || e.message,
      });
    }
  };

  const toggleUserSync = async (mu) => {
    try {
      await api.toggleManagedTeamsUser(mu.id, !mu.syncEnabled);
      await fetchManagedUsers();
    } catch (e) {
      setCallbackStatus({ type: "error", message: e.message });
    }
  };

  const removeUser = async (mu) => {
    if (!window.confirm(`Remove ${mu.email} from synced users?`)) return;
    try {
      await api.removeManagedTeamsUser(mu.id);
      await fetchManagedUsers();
    } catch (e) {
      setCallbackStatus({ type: "error", message: e.message });
    }
  };

  const fetchManagedUsers = async () => {
    try {
      const resp = await api.getManagedTeamsUsers();
      setManagedUsers(resp.users || []);
    } catch (e) {
      console.error("Failed to fetch managed users:", e);
    }
  };

  const fetchUpcomingMeetings = async () => {
    try {
      const resp = await api.getTeamsUpcomingMeetings();
      setUpcomingMeetings(
        (resp.meetings || []).map((m) => ({
          ...m,
          startTime: m.scheduledAt || m.startedAt,
        })),
      );
    } catch (e) {
      console.error("Failed to fetch meetings:", e);
    }
  };

  const openAddModal = async () => {
    setShowAddModal(true);
    setSelectedToAdd({});
    setSearchQuery("");
    await searchTenantUsers("");
  };

  const searchTenantUsers = useCallback(async (q) => {
    try {
      setSearchingUsers(true);
      const resp = await api.listTeamsTenantUsers(q);
      setTenantUsers(resp.users || []);
    } catch (e) {
      setCallbackStatus({
        type: "error",
        message: e.response?.data?.message || e.message,
      });
    } finally {
      setSearchingUsers(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (!showAddModal) return;
    const t = setTimeout(() => searchTenantUsers(searchQuery), 400);
    return () => clearTimeout(t);
  }, [searchQuery, showAddModal, searchTenantUsers]);

  const addSelectedUsers = async () => {
    const picks = Object.values(selectedToAdd).filter(Boolean);
    if (picks.length === 0) return;
    try {
      setAddingUsers(true);
      await api.addManagedTeamsUsers(picks);
      await fetchManagedUsers();
      setShowAddModal(false);
      setCallbackStatus({
        type: "success",
        message: `Added ${picks.length} user(s)`,
      });
      setTimeout(() => setCallbackStatus(null), 2500);
    } catch (e) {
      setCallbackStatus({
        type: "error",
        message: e.response?.data?.message || e.message,
      });
    } finally {
      setAddingUsers(false);
    }
  };

  const alreadyManagedIds = new Set(managedUsers.map((u) => u.microsoftUserId));

  const formatTime = (s) =>
    s
      ? new Date(s).toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "N/A";

  return (
    <div className="bg-[#0C0E12] min-h-screen text-white p-6">
      <div className="max-w-6xl mx-auto">
        {/* Status banner */}
        {callbackStatus && (
          <div
            className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
              callbackStatus.type === "success"
                ? "bg-green-600/20 border border-green-600/50"
                : callbackStatus.type === "warning"
                  ? "bg-yellow-600/20 border border-yellow-600/50"
                  : "bg-red-600/20 border border-red-600/50"
            }`}
          >
            {callbackStatus.type === "success" ? (
              <CheckCircle className="w-5 h-5 text-green-400" />
            ) : (
              <AlertCircle
                className={`w-5 h-5 ${
                  callbackStatus.type === "warning"
                    ? "text-yellow-400"
                    : "text-red-400"
                }`}
              />
            )}
            <span>{callbackStatus.message}</span>
            <button onClick={() => setCallbackStatus(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">
            Microsoft Teams Integration
          </h1>
          <p className="text-gray-400">
            Admin connects your Microsoft tenant once, then selects which users
            to sync — no per-user login required.
          </p>
        </div>

        {/* Tenant connection card */}
        <div className="bg-[#13161B] border border-gray-700 rounded-lg p-6 mb-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-600/20 rounded-lg">
                <Building2 className="w-8 h-8 text-blue-400" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-1">
                  {isConnected
                    ? "Tenant Connected"
                    : "Connect Microsoft Tenant"}
                </h3>
                {isConnected && connectionDetails ? (
                  <div className="space-y-1 text-sm text-gray-400">
                    <p className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      {connectionDetails.tenantName ||
                        connectionDetails.tenantId}
                    </p>
                    <p>
                      Managed users:{" "}
                      <span className="text-white">
                        {connectionDetails.managedUsersCount}
                      </span>
                    </p>
                    <p>
                      Last synced:{" "}
                      {connectionDetails.lastSyncedAt
                        ? new Date(
                            connectionDetails.lastSyncedAt,
                          ).toLocaleString()
                        : "Never"}
                    </p>
                    {connectionDetails.consentedBy && (
                      <p className="text-xs text-gray-500">
                        Connected by {connectionDetails.consentedBy.name}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 max-w-md">
                    A Microsoft Global Admin must grant tenant-wide consent.
                    Once granted, you can add any user in your organization
                    without them logging in individually.
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-2 flex-wrap">
              {isConnected ? (
                <>
                  <button
                    onClick={syncAll}
                    disabled={syncing || loading}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg flex items-center gap-2 disabled:opacity-50"
                  >
                    <RefreshCw
                      className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`}
                    />
                    {syncing ? "Syncing..." : "Sync All"}
                  </button>
                  <button
                    onClick={disconnectTeams}
                    disabled={loading}
                    className="px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg disabled:opacity-50"
                  >
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={connectTeams}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center gap-2 disabled:opacity-50"
                >
                  <Link2 className="w-4 h-4" />
                  {loading ? "Connecting..." : "Grant Admin Consent"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Managed users */}
        {isConnected && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Synced Users</h2>
              <button
                onClick={openAddModal}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Users
              </button>
            </div>

            {managedUsers.length === 0 ? (
              <div className="bg-[#13161B] border border-gray-700 rounded-lg p-10 text-center">
                <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400 mb-4">
                  No users added yet. Add users from your Microsoft tenant to
                  start syncing their Teams calendars.
                </p>
                <button
                  onClick={openAddModal}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg inline-flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Your First User
                </button>
              </div>
            ) : (
              <div className="bg-[#13161B] border border-gray-700 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-800/50">
                    <tr className="text-left text-xs uppercase text-gray-400">
                      <th className="px-4 py-3">User</th>
                      <th className="px-4 py-3">Last Synced</th>
                      <th className="px-4 py-3">Meetings</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {managedUsers.map((u) => (
                      <tr
                        key={u.id}
                        className="border-t border-gray-800 hover:bg-gray-800/30"
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium">
                            {u.displayName || u.email}
                          </div>
                          <div className="text-sm text-gray-400">{u.email}</div>
                          {u.jobTitle && (
                            <div className="text-xs text-gray-500">
                              {u.jobTitle}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {u.lastSyncedAt
                            ? new Date(u.lastSyncedAt).toLocaleString()
                            : "Never"}
                          {u.lastSyncError && (
                            <div className="text-xs text-red-400 mt-1">
                              {u.lastSyncError}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">{u.meetingsCount || 0}</td>
                        <td className="px-4 py-3">
                          {u.syncEnabled ? (
                            <span className="inline-flex items-center gap-1 text-green-400 text-sm">
                              <Power className="w-3 h-3" /> Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-gray-500 text-sm">
                              <PowerOff className="w-3 h-3" /> Paused
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex gap-2">
                            <button
                              onClick={() => syncOne(u.id)}
                              className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded"
                              title="Sync now"
                            >
                              <RefreshCw className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => toggleUserSync(u)}
                              className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded"
                              title={
                                u.syncEnabled ? "Pause sync" : "Resume sync"
                              }
                            >
                              {u.syncEnabled ? (
                                <PowerOff className="w-3.5 h-3.5" />
                              ) : (
                                <Power className="w-3.5 h-3.5" />
                              )}
                            </button>
                            <button
                              onClick={() => removeUser(u)}
                              className="px-2 py-1 text-xs bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded"
                              title="Remove"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Upcoming meetings */}
        {isConnected && upcomingMeetings.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Upcoming Meetings</h2>
            <div className="grid gap-3">
              {upcomingMeetings.map((meeting) => (
                <div
                  key={meeting.id}
                  className="bg-[#13161B] border border-gray-700 rounded-lg p-5 hover:border-gray-600"
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold mb-1">{meeting.title}</h3>
                      <div className="flex items-center gap-4 text-sm text-gray-400 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatTime(meeting.startTime)}
                        </span>
                        {meeting.managedUser && (
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {meeting.managedUser.email}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {meeting.status !== "recording" ? (
                        <button
                          onClick={() =>
                            api
                              .joinTeamsMeeting(meeting.id)
                              .then(fetchUpcomingMeetings)
                          }
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center gap-2 text-sm"
                        >
                          <Video className="w-4 h-4" /> Start Recording
                        </button>
                      ) : (
                        <button
                          onClick={() =>
                            api
                              .leaveTeamsMeeting(meeting.id)
                              .then(fetchUpcomingMeetings)
                          }
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm"
                        >
                          Stop Recording
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add Users Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#13161B] border border-gray-700 rounded-lg w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-gray-700">
              <h3 className="text-lg font-semibold">Add Tenant Users</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 border-b border-gray-700">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-3">
              {searchingUsers ? (
                <div className="p-8 text-center text-gray-400">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                  Loading users...
                </div>
              ) : tenantUsers.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  No users found
                </div>
              ) : (
                tenantUsers.map((u) => {
                  const already = alreadyManagedIds.has(u.microsoftUserId);
                  const checked = !!selectedToAdd[u.microsoftUserId];
                  return (
                    <label
                      key={u.microsoftUserId}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer ${
                        already
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:bg-gray-800"
                      }`}
                    >
                      <input
                        type="checkbox"
                        disabled={already}
                        checked={checked}
                        onChange={(e) => {
                          setSelectedToAdd((prev) => {
                            const next = { ...prev };
                            if (e.target.checked) next[u.microsoftUserId] = u;
                            else delete next[u.microsoftUserId];
                            return next;
                          });
                        }}
                        className="w-4 h-4"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">
                          {u.displayName || u.email}
                        </div>
                        <div className="text-sm text-gray-400 truncate">
                          {u.email}
                        </div>
                      </div>
                      {already && (
                        <span className="text-xs text-gray-500">
                          Already added
                        </span>
                      )}
                    </label>
                  );
                })
              )}
            </div>

            <div className="p-5 border-t border-gray-700 flex justify-between items-center">
              <span className="text-sm text-gray-400">
                {Object.keys(selectedToAdd).length} selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={addSelectedUsers}
                  disabled={
                    addingUsers || Object.keys(selectedToAdd).length === 0
                  }
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
                >
                  {addingUsers ? "Adding..." : "Add Selected"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamsIntegration;
